/**
 * Analytics Dashboard Component
 * Display visitor tracking data and statistics
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  BarChart3, 
  Users, 
  Eye, 
  Clock, 
  Globe, 
  Monitor, 
  Smartphone,
  RefreshCw,
  Download,
  Trash2
} from 'lucide-react';
import { useVisitorTracking } from '../hooks/use-visitor-tracking';

export function AnalyticsDashboard() {
  const { visitorData, pageViews, trackedEvents, clearData, refreshData } = useVisitorTracking();
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = () => {
    refreshData();
    setRefreshKey(prev => prev + 1);
  };

  const handleExportData = () => {
    const exportData = {
      visitorData,
      pageViews,
      trackedEvents,
      exportDate: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-data-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const getDeviceType = (userAgent: string): string => {
    if (/Mobile|Android|iPhone|iPad/.test(userAgent)) {
      return 'Mobile';
    } else if (/Tablet/.test(userAgent)) {
      return 'Tablet';
    } else {
      return 'Desktop';
    }
  };

  const getBrowser = (userAgent: string): string => {
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    return 'Other';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6" />
            Analytics Dashboard
          </h2>
          <p className="text-muted-foreground">
            Real-time visitor tracking and analytics data
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportData}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button variant="destructive" size="sm" onClick={clearData}>
            <Trash2 className="h-4 w-4 mr-2" />
            Clear Data
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Visitor ID</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{visitorData.visitorId.split('_')[1]}</div>
            <p className="text-xs text-muted-foreground">
              Unique identifier
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Visit Count</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{visitorData.visitCount}</div>
            <p className="text-xs text-muted-foreground">
              Total visits
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Page Views</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{visitorData.pageViews}</div>
            <p className="text-xs text-muted-foreground">
              Current session: {pageViews.length}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Session Duration</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatDuration(visitorData.sessionDuration)}</div>
            <p className="text-xs text-muted-foreground">
              Current session
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Information */}
      <Tabs defaultValue="visitor" className="space-y-4">
        <TabsList>
          <TabsTrigger value="visitor">Visitor Info</TabsTrigger>
          <TabsTrigger value="pageviews">Page Views</TabsTrigger>
          <TabsTrigger value="events">Events</TabsTrigger>
          <TabsTrigger value="technical">Technical</TabsTrigger>
        </TabsList>

        <TabsContent value="visitor" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Visit Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">First Visit:</span>
                  <span className="text-sm">{new Date(visitorData.firstVisit).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Last Visit:</span>
                  <span className="text-sm">{new Date(visitorData.lastVisit).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Session Start:</span>
                  <span className="text-sm">{new Date(visitorData.sessionStartTime).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Referrer:</span>
                  <span className="text-sm">{visitorData.referrer || 'Direct'}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Location & Language</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Language:</span>
                  <Badge variant="secondary">{visitorData.language}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Timezone:</span>
                  <Badge variant="secondary">{visitorData.timezone}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Current Page:</span>
                  <Badge variant="outline">{visitorData.currentPage}</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="pageviews" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Page Views (Current Session)</CardTitle>
              <CardDescription>
                Pages visited during this session with timestamps and duration
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {pageViews.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No page views recorded</p>
                ) : (
                  pageViews.map((view, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <span className="font-medium">{view.page}</span>
                        <p className="text-sm text-muted-foreground">
                          {new Date(view.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge variant="secondary">
                          {view.duration > 0 ? formatDuration(Math.round(view.duration / 1000)) : 'Active'}
                        </Badge>
                        {view.referrer !== 'direct' && (
                          <p className="text-xs text-muted-foreground mt-1">
                            from: {view.referrer}
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="events" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Tracked Events</CardTitle>
              <CardDescription>
                User interactions and custom events tracked during this session
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {trackedEvents.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No events tracked</p>
                ) : (
                  trackedEvents.map((event, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <span className="font-medium">{event.name}</span>
                        <p className="text-sm text-muted-foreground">
                          {new Date(event.timestamp).toLocaleTimeString()} • {event.page}
                        </p>
                      </div>
                      <div className="text-right">
                        {event.data && (
                          <Badge variant="outline">
                            {typeof event.data === 'object' 
                              ? Object.keys(event.data).length + ' properties'
                              : event.data
                            }
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="technical" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Monitor className="h-4 w-4" />
                  Device Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Device Type:</span>
                  <Badge variant="secondary">
                    {getDeviceType(visitorData.userAgent)}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Screen Resolution:</span>
                  <Badge variant="outline">{visitorData.screenResolution}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Browser:</span>
                  <Badge variant="secondary">
                    {getBrowser(visitorData.userAgent)}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Session Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Session ID:</span>
                  <span className="text-sm font-mono">{visitorData.sessionId.split('_')[1]}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Visitor ID:</span>
                  <span className="text-sm font-mono">{visitorData.visitorId.split('_')[1]}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>User Agent</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm font-mono bg-muted p-3 rounded-lg break-all">
                {visitorData.userAgent}
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}